import { useCallback, useState } from 'react';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';

const DEFAULT_ALLOWED_TYPES = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
const DEFAULT_MAX_SIZE = 5 * 1024 * 1024; // 5 MB

export const useImportExport = ({
  allowedMimeTypes = DEFAULT_ALLOWED_TYPES,
  maxFileSize = DEFAULT_MAX_SIZE,
  onImport,
  onExport,
  onImportSuccess,
  onImportError,
} = {}) => {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const { showCreate } = useMessageAlert();

  const validateFile = useCallback(
    (file) => {
      /**
       * Validate an uploaded file against allowed MIME types and size limits.
       * Returns an object { valid: boolean, reason?: string }
       */
      if (!file) {
        return { valid: false, reason: 'NO_FILE' };
      }

      const extension = file.name.split('.').pop()?.toLowerCase();
      const matchesExtension = ['xls', 'xlsx'].includes(extension ?? '');
      const matchesMime = allowedMimeTypes.includes(file.type) || file.type === '';

      if (!matchesExtension && !matchesMime) {
        return { valid: false, reason: 'INVALID_TYPE' };
      }

      if (file.size > maxFileSize) {
        return { valid: false, reason: 'SIZE_EXCEEDED' };
      }

      return { valid: true };
    },
    [allowedMimeTypes, maxFileSize]
  );

  const resetImportState = useCallback(() => {
    setImportError(null);
    setImportSuccess(false);
  }, []);

  const handleImport = useCallback(
    async (file) => {
      /**
       * Handle import workflow.
       * - Validates the file, calls `onImport`, shows success notifications, and
       *   calls callbacks on success/failure.
       */
      resetImportState();

      const validation = validateFile(file);
      if (!validation.valid) {
        setImportError(validation.reason);
        onImportError?.(validation.reason);
        return { success: false, error: validation.reason };
      }

      if (typeof onImport !== 'function') {
        return { success: false, error: 'IMPORT_NOT_CONFIGURED' };
      }

      setImporting(true);
      setImportError(null);
      try {
        const result = await onImport(file);

        // If API returns structured errors (array) or indicates total_errors, surface them
        if (result && (Array.isArray(result.errors) || (typeof result.total_errors === 'number' && result.total_errors > 0))) {
          const serverErrors = result.errors || [];
          // set importError to the errors array so UI can render them
          setImportError(serverErrors.length ? serverErrors : (result.message || 'IMPORT_FAILED'));
          onImportError?.(serverErrors.length ? serverErrors : (result.message || 'IMPORT_FAILED'));
          return { success: false, error: serverErrors.length ? serverErrors : (result.message || 'IMPORT_FAILED'), data: result };
        }

        if (result?.success) {
          showCreate(i18n.t('common.notifications.importCompleted'));
          setImportSuccess(true);
          onImportSuccess?.(result.data);
          return { success: true, data: result.data };
        }

        const errorReason = result?.error || 'IMPORT_FAILED';
        setImportError(errorReason);
        onImportError?.(errorReason);
        return { success: false, error: errorReason };
      } catch (error) {
        const reason = error?.message || 'IMPORT_FAILED';
        setImportError(reason);
        onImportError?.(reason);
        return { success: false, error: reason };
      } finally {
        setImporting(false);
      }
    },
    [onImport, onImportError, onImportSuccess, resetImportState, validateFile, showCreate]
  );

  const handleExport = useCallback(async () => {
    /**
     * Handle export workflow.
     * - Calls `onExport` and displays a success notification if configured.
     */
    if (typeof onExport !== 'function') {
      return { success: false, error: 'EXPORT_NOT_CONFIGURED' };
    }

    setExporting(true);
    try {
      const result = await onExport();
      if (result?.success !== false) {
  showCreate(i18n.t('common.notifications.exportCompleted'));
        return { success: true };
      }
      return { success: false, error: result?.error };
    } catch (error) {
      return { success: false, error: error?.message || 'EXPORT_FAILED' };
    } finally {
      setExporting(false);
    }
  }, [onExport, showCreate]);

  return {
    importing,
    exporting,
    importError,
    importSuccess,
    handleImport,
    handleExport,
    resetImportState,
  };
};

export default useImportExport;
